import React from "react";
import { Eye, X } from "lucide-react";
import { api, setStoredUser } from "../lib/api";
import { toast } from "sonner";

/**
 * useImpersonate — admin-only hook to preview a brand portal as a real brand user.
 * The backend swaps the auth cookie to a 30-min brand JWT and stashes the original
 * admin JWT in a separate httpOnly cookie. exitPreview() calls back to restore.
 */
export function useImpersonate() {
  const previewAs = async ({ brandId, teamUserId, brandName } = {}) => {
    try {
      const { data } = await api.post("/admins/impersonate", { brand_id: brandId, team_user_id: teamUserId });
      setStoredUser(data.user);
      toast.success(`Previewing as ${data.user.email}${brandName ? ` (${brandName})` : ""}`);
      // Hard reload so AuthProvider re-fetches /auth/me with the new cookie
      // and the brand-side context renders fresh.
      window.location.assign("/campaigns");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not start preview");
    }
  };

  const exitPreview = async () => {
    try {
      const { data } = await api.post("/auth/exit-impersonate");
      setStoredUser(data.user);
      toast.success("Back to Wishlink Admin");
      window.location.assign("/admin/brands");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No admin session to return to");
    }
  };

  return { previewAs, exitPreview };
}

export function ImpersonationBanner({ user }) {
  const { exitPreview } = useImpersonate();
  if (!user?.impersonated_by) return null;
  return (
    <div
      className="sticky top-0 z-50 w-full text-sm font-medium px-6 py-2.5 flex items-center justify-center gap-3 border-b"
      style={{ background: "#fef3c7", color: "#7c2d12", borderColor: "#fcd34d" }}
      data-testid="impersonation-banner"
    >
      <Eye size={14} />
      <span>
        Admin preview · viewing as <span className="font-semibold">{user.email}</span>{user.brand?.name ? ` for ${user.brand.name}` : ""}. Session expires in 30 minutes.
      </span>
      <button
        onClick={exitPreview}
        className="ml-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/80 hover:bg-white border border-[#fcd34d] text-[11px] font-semibold uppercase tracking-wider"
        data-testid="impersonation-exit"
      >
        <X size={12} /> Exit preview
      </button>
    </div>
  );
}
